import { useEffect, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { warmImageCache } from '../../utils/prefetch-images';

interface UnitPhotoCarouselProps {
  photos: string[];
  label: string;
  /** Extra classes for the inline frame. Ignored when `fill` is set. */
  className?: string;
  /** Stretch to the parent instead of a fixed aspect ratio. */
  fill?: boolean;
}

export default function UnitPhotoCarousel({
  photos,
  label,
  className = '',
  fill = false,
}: UnitPhotoCarouselProps): ReactElement {
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const count = photos.length;
  const currentIndex = count === 0 ? 0 : ((index % count) + count) % count;
  const current = photos[currentIndex];

  const go = (delta: number) => {
    if (count < 2) return;
    setIndex((value) => (value + delta + count) % count);
  };

  useEffect(() => warmImageCache(photos), [photos]);

  useEffect(() => {
    if (!expanded) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false);
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (count < 2) return;
      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -1 : 1;
      setIndex((value) => (value + delta + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [expanded, count]);

  const controls = () =>
    count > 1 ? (
      <>
        <button
          type="button"
          aria-label="Previous photo"
          onClick={(event) => {
            event.stopPropagation();
            go(-1);
          }}
          className="pointer-events-auto absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Next photo"
          onClick={(event) => {
            event.stopPropagation();
            go(1);
          }}
          className="pointer-events-auto absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow"
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
        <p className="absolute bottom-3 right-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
          {currentIndex + 1} / {count}
        </p>
      </>
    ) : null;

  const slides = photos.length > 0 ? (
    photos.map((src, photoIndex) => {
      const active = photoIndex === currentIndex;
      return (
        <img
          key={`${src}-${photoIndex}`}
          src={src}
          alt={active ? `${label}, photo ${photoIndex + 1} of ${count}` : ''}
          aria-hidden={active ? undefined : true}
          loading="eager"
          decoding="async"
          className={`absolute inset-0 h-full w-full object-cover ${active ? 'opacity-100' : 'opacity-0'}`}
        />
      );
    })
  ) : (
    <p className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-gray-500 dark:text-gray-400">
      No photo
    </p>
  );

  return (
    <>
      <div className={`relative overflow-hidden bg-gray-100 dark:bg-gray-900 ${fill ? 'h-full min-h-0 rounded-xl' : className}`}>
        {slides}
        {current ? (
          <button
            type="button"
            aria-label="View photos full screen"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded(true);
            }}
            className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow"
          >
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
        {controls()}
      </div>
      {expanded && current
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-label={`${label} photos`}
              className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4 sm:p-8"
              onClick={() => setExpanded(false)}
            >
              <button
                type="button"
                aria-label="Close full screen"
                onClick={(event) => {
                  event.stopPropagation();
                  setExpanded(false);
                }}
                className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
              <img
                src={current}
                alt={`${label}, photo ${currentIndex + 1} of ${count}`}
                className="max-h-full max-w-full object-contain"
                onClick={(event) => event.stopPropagation()}
              />
              <div className="pointer-events-none absolute inset-0">{controls()}</div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
